import { cn } from "@/utils/theme";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  inputVariants,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import { DropdownListItem, type IDropdownListItem } from "./dropdown-list-item";
import { DropdownListSeparator } from "./dropdown-list-separator";
import type { ComponentProps } from "react";
import { useState } from "react";
import type { VariantProps } from "class-variance-authority";
import { buttonVariants } from "@elaniintc/goes-design-system";

interface DropdownListGroup {
  groupId: string;
  groupLabels: string[];
  items: IDropdownListItem[];
  icon?: ComponentProps<typeof Icon> & {
    variant?:
      | "info"
      | "warning"
      | "disabled"
      | "action"
      | "success"
      | "danger"
      | "neutral"
      | null
      | undefined;
  };
}

type DropdownListOptions = IDropdownListItem[] | DropdownListGroup[];
type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

/**
 * Type guard to check if options are grouped
 *
 * @param options - Array of dropdown items or groups.
 * @returns
 */
const isGroupedOptions = (
  options: DropdownListOptions,
): options is DropdownListGroup[] => {
  return options.length > 0 && "groupId" in options[0];
};

interface DropdownListProps {
  options: DropdownListOptions;
  group?:
    | "middle"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "down-left"
    | "down-right"
    | "middle-left"
    | "middle-right";
  iconPosition?: "left" | "right";
  widthClassName?: string; // css classname or tailwind style e.g., "w-full", "w-64"
  heightClassName?: string; // css classname or tailwind style e.g., "h-64", "h-full"
  scrollClassName?: string; // css classname or tailwind style e.g., "h-64", "h-full"
  disabled?: boolean;
  search?: {
    placeholder: string;
    query: string;
    onSearch: (query: string) => void;
  };
  fetchMore?: {
    hasMore: boolean;
    isFetching: boolean;
    fetchNextPage: () => void;
  };
  placeholder?: string;
  selectedValue?: IDropdownListItem;
  selectedGroup?: DropdownListGroup;
  buttonVariant?: ButtonVariant;
  toggleSelection?: (
    value: IDropdownListItem,
    group?: DropdownListGroup,
  ) => void;
  labelClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const DropdownList = ({
  options,
  iconPosition = "left",
  search,
  fetchMore,
  widthClassName,
  heightClassName,
  scrollClassName,
  placeholder,
  toggleSelection,
  selectedValue,
  selectedGroup,
  buttonVariant,
  group,
  disabled,
  labelClassName = "",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: DropdownListProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const handleItemClick = (
    item: IDropdownListItem,
    group?: DropdownListGroup,
  ) => {
    if (toggleSelection) {
      toggleSelection(item, group);
    }
    setOpen(false); // Close dropdown after selection
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled}
          group={group}
          variant={buttonVariant || "tertiary"}
          className={cn(
            "flex justify-start items-center group min-w-0 relative gap-2",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                widthClassName,
              ),
              group,
            }),
          )}
        >
          {selectedGroup?.icon && (
            <Badge
              shape="pill"
              size="sm"
              variant={selectedGroup.icon.variant || "action"}
              className={cn(
                "min-w-6 px-1 flex justify-center items-center text-body",
              )}
            >
              <Icon {...selectedGroup.icon} />
            </Badge>
          )}

          {selectedValue?.icon && (
            <Icon {...selectedValue.icon} className="text-body" />
          )}
          <p
            className={cn(
              "truncate w-full font-normal pr-8 text-start",
              selectedValue?.label ? "text-body" : "text-body-quiet",
              labelClassName,
            )}
          >
            {selectedValue?.label ?? placeholder}
          </p>

          <Icon
            type="far"
            name="angle-down"
            className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col p-0 rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl h-auto",
          heightClassName,
        )}
        style={{
          width:
            "var(--radix-popper-anchor-width, var(--radix-dropdown-menu-trigger-width))",
        }}
        side="bottom"
        align="start"
      >
        {search && (
          <div className="border-b border-neutral-quiet p-3 relative ">
            <Icon
              type="far"
              name="search"
              className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
            />
            <Input
              variant="sm"
              className="text-sm w-full font-normal flex-auto pl-9"
              placeholder={search.placeholder || "Buscar..."}
              onChange={(e) => search.onSearch(e.target.value)}
              value={search.query}
            />
          </div>
        )}
        <ScrollArea className={cn("p-2", scrollClassName)}>
          {/* <div className="flex-auto h-full flex flex-col p-2 gap-2"></div> */}
          {isGroupedOptions(options) ? (
            // Render grouped options
            options.map((group) => (
              <div key={group.groupId} className="flex flex-col gap-2 mb-2">
                <DropdownListSeparator
                  options={group.groupLabels}
                  icon={group.icon}
                />
                {group.items.map((item) => (
                  <DropdownListItem
                    key={item.value}
                    iconPosition={iconPosition}
                    {...item}
                    toggleSelection={(item) => handleItemClick(item, group)}
                  />
                ))}
              </div>
            ))
          ) : options.length > 0 ? (
            // Render simple item list
            options.map((item) => (
              <DropdownListItem
                key={item.value}
                iconPosition={iconPosition}
                {...item}
                toggleSelection={(item) => handleItemClick(item)}
              />
            ))
          ) : (
            <div className="flex items-center justify-center py-5">
              <span className="text-body-quiet">
                No se encontraron coincidencias
              </span>
            </div>
          )}
          {fetchMore && fetchMore.hasMore && (
            <div className="w-full flex items-center justify-start">
              <Button
                variant="ghost-blue"
                size="xs"
                type="button"
                disabled={fetchMore.isFetching}
                className="shadow-none"
                onClick={() => {
                  if (fetchMore.isFetching) return;
                  fetchMore.fetchNextPage();
                }}
              >
                Más resultados
                <Icon
                  type="far"
                  name={fetchMore.isFetching ? "spinner-third" : "plus"}
                  className={cn(
                    "text-current",
                    fetchMore.isFetching && "animate-spin",
                  )}
                />
              </Button>
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { DropdownList };
export type { IDropdownListItem, DropdownListGroup, DropdownListOptions };
